import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { cacheGet, cachePut } from "@/lib/offlineCache";
import { enqueueAction } from "@/lib/offline";
import { startQrScanner, requestCameraPermission, classifyCameraError, CAMERA_ERROR_KEYS } from "@/lib/qrCamera";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Camera, Upload, ShieldAlert } from "lucide-react";

const SCANNER_ID = "am-qr-scanner";

export default function QRScannerDialog({ open, onOpenChange }) {
  const { t } = useT();
  const scannerRef = useRef(null);
  const fileRef = useRef(null);
  const retryTimerRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [result, setResult] = useState(null);
  // Synchronous mirror of `result` — read inside timers to avoid stale closures.
  const resultRef = useRef(null);
  resultRef.current = result;

  const stop = async () => {
    if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
    const s = scannerRef.current;
    if (s) {
      try { if (s.isScanning) await s.stop(); } catch (err) { console.debug("qr stop", err); }
      try { s.clear(); } catch (err) { console.debug("qr clear", err); }
      scannerRef.current = null;
    }
    setRunning(false);
  };

  const lookupPayload = async (decodedText) => {
    try {
      const r = await api.post("/helper/scan-entry", null, { params: { qr_payload: decodedText } });
      const action = r.data.action;
      const msg = action === "entry" ? t("qr.entryOk") : action === "exit" ? t("qr.exitOk") : (r.data.message || t("qr.success"));
      setResult({ status: "ok", action, data: r.data.gatepass, message: msg });
      toast.success(msg);
    } catch (e) {
      if (!e?.response) {
        // Offline — the SAME QR works for entry AND exit. Determine the intended
        // transition from the cached pass status, queue it (duplicate-safe on
        // sync via expected_action) and update the local cache.
        const cached = await cacheGet("/helper/gatepasses");
        const list = Array.isArray(cached) ? cached : null;
        const gp = list ? list.find((g) => g.qr_payload === decodedText || g.pass_number === decodedText) : null;
        if (gp) {
          const status = gp.status || (gp.scanned_at ? "entered" : "registered");
          if (status === "exited") {
            setResult({ status: "err", message: t("qr.alreadyExited") });
            return;
          }
          const action = status === "registered" ? "entry" : "exit";
          await enqueueAction("/helper/scan-entry", { qr_payload: decodedText, expected_action: action }, { entity: "scan_entry" });
          const now = new Date().toISOString();
          const updated = action === "entry"
            ? { ...gp, status: "entered", entry_time: now, scanned_at: now }
            : { ...gp, status: "exited", exit_time: now };
          // Persist locally so a later offline scan of the same QR records the exit.
          await cachePut("/helper/gatepasses", list.map((g) => (g.gatepass_id === gp.gatepass_id ? updated : g)));
          const msg = action === "entry" ? t("qr.entryOffline") : t("qr.exitOffline");
          setResult({ status: "ok", action, data: updated, message: msg });
          toast.success(msg);
        } else {
          setResult({ status: "err", message: t("qr.offlineNotFound") });
        }
        return;
      }
      setResult({ status: "err", message: e?.response?.data?.detail || t("qr.notFound") });
    }
  };

  const start = async (attempt = 0) => {
    setResult(null);
    setCameraError(null);
    if (running) return;
    try {
      setRunning(true);
      scannerRef.current = await startQrScanner(
        SCANNER_ID,
        async (decodedText) => { await stop(); await lookupPayload(decodedText); },
        { qrbox: 260 }
      );
    } catch (e) {
      setRunning(false);
      const code = classifyCameraError(e);
      // Auto-retry once — transient failures (camera warming up / just released).
      if (attempt === 0 && code !== "denied" && code !== "https" && code !== "nocamera") {
        // The retry must never wipe a result produced meanwhile by a file upload.
        retryTimerRef.current = setTimeout(() => { if (!resultRef.current) start(1); }, 1200);
        return;
      }
      setCameraError(code);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    await stop(); // also cancels any pending camera auto-retry
    try {
      const html5 = new Html5Qrcode(SCANNER_ID);
      scannerRef.current = html5;
      const decoded = await html5.scanFile(file, true);
      try { html5.clear(); } catch (err) { console.debug("qr clear", err); }
      scannerRef.current = null;
      await lookupPayload(decoded);
    } catch (e) {
      setResult({ status: "err", message: t("qr.notFound") });
    }
  };

  // Effect keys on `open` only — a latest-callback ref keeps start/stop fresh
  // without re-running the camera lifecycle on unrelated re-renders.
  const lifecycleRef = useRef({ start, stop });
  lifecycleRef.current = { start, stop };
  useEffect(() => {
    const cam = lifecycleRef.current;
    if (open) cam.start();
    return () => { cam.stop(); };
  }, [open]);

  const allowCamera = async () => {
    try {
      await requestCameraPermission();
      start();
    } catch (e) {
      setCameraError(classifyCameraError(e));
      toast.error(t("qr.errDenied"));
    }
  };

  const reset = () => {
    setResult(null);
    setCameraError(null);
    start();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) stop(); onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="qr-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{t("qr.title")}</DialogTitle>
          <DialogDescription>{t("qr.aim")}</DialogDescription>
        </DialogHeader>

        {!result && (
          <>
            {cameraError ? (
              <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-center space-y-3" data-testid="qr-camera-error">
                <ShieldAlert className="w-8 h-8 text-amber-700 mx-auto" />
                <p className="text-sm font-semibold text-amber-900" data-testid={`qr-camera-error-${cameraError}`}>
                  {t(CAMERA_ERROR_KEYS[cameraError] || "qr.cameraBlocked")}
                </p>
                {cameraError === "denied" ? (
                  <Button className="am-btn-primary w-full" onClick={allowCamera} data-testid="qr-allow-camera-btn">
                    <Camera className="w-4 h-4 mr-2" /> {t("qr.allowCamera")}
                  </Button>
                ) : cameraError !== "https" && cameraError !== "nocamera" ? (
                  <Button className="am-btn-primary w-full" onClick={() => start()} data-testid="qr-retry-camera-btn">
                    <Camera className="w-4 h-4 mr-2" /> {t("qr.retryCamera")}
                  </Button>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t("qr.aim")}</p>
            )}
            <div id={SCANNER_ID} className="w-full rounded-2xl overflow-hidden bg-black" style={{ minHeight: cameraError ? 0 : 280, display: cameraError ? "none" : "block" }} />
            <div className="pt-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="qr-upload-input"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              <Button
                data-testid="qr-upload-btn"
                variant="outline"
                className="w-full rounded-full"
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="w-4 h-4 mr-2" /> {t("qr.uploadImage")}
              </Button>
            </div>
          </>
        )}

        {result?.status === "ok" && (
          <div className="text-center py-4 space-y-3">
            <CheckCircle2 className="w-14 h-14 text-primary mx-auto" />
            <div className="font-display font-bold text-xl">{result.message}</div>
            {result.data && (
              <div className="text-sm text-left bg-muted/50 rounded-2xl p-4 space-y-1 font-mono">
                <div><span className="text-muted-foreground">Pass:</span> <b>{result.data.pass_number}</b></div>
                <div data-testid="qr-result-status">
                  <span className="text-muted-foreground">Status:</span>{" "}
                  <b className="uppercase">{result.data.status || (result.data.scanned_at ? "entered" : "registered")}</b>
                </div>
                {result.data.entry_time && (
                  <div className="text-xs text-muted-foreground">Entry: {new Date(result.data.entry_time).toLocaleString("en-IN")}</div>
                )}
                {result.data.exit_time && (
                  <div className="text-xs text-muted-foreground">Exit: {new Date(result.data.exit_time).toLocaleString("en-IN")}</div>
                )}
                <div className="text-xs text-muted-foreground break-all">QR: {result.data.qr_payload}</div>
              </div>
            )}
            <Button className="am-btn-primary w-full" onClick={reset} data-testid="qr-rescan-btn">
              <Camera className="w-4 h-4 mr-2" /> {t("qr.scanAgain")}
            </Button>
          </div>
        )}

        {result?.status === "err" && (
          <div className="text-center py-4 space-y-3">
            <XCircle className="w-14 h-14 text-destructive mx-auto" />
            <div className="font-display font-bold text-xl">{result.message}</div>
            <div className="flex gap-2">
              <Button className="am-btn-primary flex-1" onClick={reset} data-testid="qr-retry-btn">
                <Camera className="w-4 h-4 mr-2" /> {t("qr.tryAgain")}
              </Button>
              <Button variant="outline" className="flex-1 rounded-full" onClick={() => fileRef.current?.click()}>
                <Upload className="w-4 h-4 mr-2" /> {t("qr.uploadImage")}
              </Button>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
